import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { Link } from 'react-router-dom';
import { MOCKUP_CLASS } from '../../mockupLook/tokens';

/**
 * Pojedyncza liczba na pulpicie.
 *
 * Każdy kafelek prowadzi do KONKRETNEGO widoku z filtrem. Liczba, w którą nie
 * da się kliknąć, zmusza planistę do ręcznego odtwarzania filtra w banku —
 * i po dwóch takich próbach przestaje się jej używać.
 */
/**
 * Etykieta ma miejsce na dwie linie, także gdy mieści się w jednej.
 *
 * Przy 210 px „Przewoźnicy w kartotece” łamie się na dwie linie, a sąsiad
 * „Oddane przewoźnikowi” nie — liczby w jednym rzędzie stały wtedy na różnej
 * wysokości (28.09.2026). `overline` ma domyślnie interlinię 2,66, więc dwie
 * linie zajmowały tyle co trzy; ściskamy ją do 1,6.
 */
export const TILE_LABEL_SX = {
  display: 'block',
  lineHeight: 1.6,
  minHeight: '3.2em',
} as const;

export const Tile = ({
  label,
  value: wartosc,
  description,
  linkTo: dokad,
  highlight: highlightKind,
  loading: ladowanie,
  testId,
  detail,
  note,
}: {
  label: string;
  value?: number;
  description?: string;
  linkTo?: string;
  highlight?: 'uwaga' | 'ostrzezenie';
  loading?: boolean;
  /**
   * `data-testid` kafelka — dla e2e i kroków przewodnika po zmianach
   * (integracja fali C, 22.10.2026); bez niego kafelek jak dotąd.
   */
  testId?: string;
  /**
   * Dopisek pod opisem, np. z kiedy są dane („Dane z Transporeonu z dnia …”,
   * 04.11.2026) — liczba bez niego wyglądałaby na bieżącą.
   */
  note?: string;
  /** Rozbicie liczby, np. na spółki („Spółka A: 3 · Spółka B: 0”, 05.11.2026). */
  detail?: string;
}) => {
  const accentColor =
    highlightKind === 'ostrzezenie'
      ? 'warning.main'
      : highlightKind === 'uwaga'
        ? 'info.main'
        : 'text.primary';

  const content = (
    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
      <Typography variant="overline" color="text.secondary" className={MOCKUP_CLASS.tileLabel} sx={TILE_LABEL_SX}>
        {label}
      </Typography>
      {ladowanie ? (
        <Skeleton variant="text" width={72} height={44} />
      ) : (
        <Typography variant="h4" className={MOCKUP_CLASS.tileValue} sx={{ color: accentColor, fontWeight: 600 }}>
          {wartosc ?? '—'}
        </Typography>
      )}
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
      {detail && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} data-testid="tile-detail">
          {detail}
        </Typography>
      )}
      {note && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }} data-testid="tile-note">
          {note}
        </Typography>
      )}
    </CardContent>
  );

  if (!dokad || ladowanie) {
    return (
      <Card variant="outlined" data-testid={testId} className={MOCKUP_CLASS.tile}>
        {content}
      </Card>
    );
  }
  return (
    <Card
      variant="outlined"
      data-testid={testId}
      className={MOCKUP_CLASS.tile}
      sx={{
        transition: 'border-color .15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        component={Link}
        to={dokad}
        sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        {content}
      </Box>
    </Card>
  );
};

/**
 * Siatka kafelków — tyle kolumn, ile się zmieści, każda co najmniej 210 px.
 *
 * Sztywne cztery kolumny zostawiały piąty kafelek „Zleceń” samotnie
 * w drugim rzędzie już przy 1366 px, a na szerokim monitorze kafelki
 * rozciągały się na ćwierć ekranu. Puste ścieżki (`auto-fill`) trzymają
 * jednakową szerokość kafelków we wszystkich sekcjach, także tych z dwoma.
 */
export const TILE_GRID_COLUMNS = 'repeat(auto-fill, minmax(210px, 1fr))';

export const TileGrid = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: '1fr', sm: TILE_GRID_COLUMNS },
    }}
  >
    {children}
  </Box>
);
